import React, { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { WfButton, CreditChip } from "@/components/site/primitives";
import { Link } from "react-router-dom";

const LAYERS = [
  { src: "/images/wf/vlai/vlai-3591.jpg", alt: "A smiling farmer in a floral sun hat harvests peppers in a lush green field." },
  { src: "/images/wf/vlai/vlai-3764.jpg", alt: "A farmer walks among tall trellised vines beneath a bright summer sky." },
  { src: "/images/wf/vlai/vlai-3527.jpg", alt: "A wide view of farmers working rows of crops with rolling hills beyond." },
];

export const HeroV2 = ({ scrollVh = 240 }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });

  const op1 = useTransform(scrollYProgress, [0, 0.28, 0.4], [1, 1, 0]);
  const op2 = useTransform(scrollYProgress, [0.28, 0.42, 0.66, 0.76], [0, 1, 1, 0]);
  const op3 = useTransform(scrollYProgress, [0.66, 0.8, 1], [0, 1, 1]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.18, 1.42]);
  const scrim = useTransform(scrollYProgress, [0, 0.5, 1], [0.5, 0.4, 0.66]);
  const hColor = useTransform(scrollYProgress, [0, 0.5, 1], ["#FFFDF8", "#F4ECDD", "#E9DFC9"]);
  const wColor = useTransform(scrollYProgress, [0, 0.4, 0.85], ["#E7A18C", "#AEC28C", "#F0E6CE"]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const contentOp = useTransform(scrollYProgress, [0, 0.72, 0.95], [1, 1, 0]);
  const cueOp = useTransform(scrollYProgress, [0, 0.12], [1, 0]);

  const opacities = [op1, op2, op3];

  return (
    <section id="top" ref={ref} style={{ height: `${scrollVh}vh` }} className="relative bg-[var(--wf-soil)]">
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        {/* Cross-fading full-bleed layers */}
        {LAYERS.map((layer, i) => (
          <motion.img
            key={layer.src}
            src={layer.src}
            alt={i === 0 ? layer.alt : ""}
            aria-hidden={i === 0 ? undefined : "true"}
            loading="eager"
            style={reduce ? { opacity: i === 0 ? 1 : 0 } : { opacity: opacities[i], scale }}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ))}

        {/* Readability scrim */}
        <motion.div aria-hidden="true" className="absolute inset-0 bg-black" style={reduce ? { opacity: 0.5 } : { opacity: scrim }} />
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />

        {/* Centered content */}
        <motion.div
          style={reduce ? undefined : { y: contentY, opacity: contentOp }}
          className="relative z-10 mx-auto flex h-full max-w-4xl flex-col items-center justify-center px-6 text-center"
        >
          <span className="mb-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.24em] text-[rgba(255,253,248,0.85)]">
            <span aria-hidden="true" className="h-px w-8 bg-[rgba(255,253,248,0.6)]" />
            World Farmers · Lancaster, MA
            <span aria-hidden="true" className="h-px w-8 bg-[rgba(255,253,248,0.6)]" />
          </span>

          <motion.h1
            data-testid="hero-headline"
            style={reduce ? { color: "#FFFDF8" } : { color: hColor }}
            className="font-serif text-[2.6rem] font-semibold leading-[1.02] tracking-[-0.01em] sm:text-6xl lg:text-7xl"
          >
            Building Farmer{" "}
            <motion.span style={reduce ? { color: "#E7A18C" } : { color: wColor }} className="italic">
              Capacity
            </motion.span>
          </motion.h1>

          <p className="mt-4 font-serif text-xl italic text-[rgba(255,253,248,0.92)] sm:text-2xl [text-shadow:0_1px_16px_rgba(0,0,0,0.5)]">
            Grounded in Community and Land
          </p>

          <p className="mt-6 max-w-2xl text-base leading-7 text-[rgba(255,253,248,0.9)] sm:text-lg [text-shadow:0_1px_16px_rgba(0,0,0,0.5)]">
            Since 1984, World Farmers has supported small-scale farmers to access land, grow the crops of their culture, and reach the markets and communities that sustain them.
          </p>

          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <WfButton as={Link} to="/support" withArrow variant="light" data-testid="hero-primary-cta-button">
              Get Involved
            </WfButton>
            <WfButton
              as={Link}
              to="/where-to-buy"
              data-testid="hero-secondary-cta-button"
              className="border border-white/60 bg-white/10 text-white backdrop-blur-sm hover:bg-white/20"
            >
              Where to buy crops
            </WfButton>
          </div>
        </motion.div>

        {/* Photo credit */}
        <CreditChip src={LAYERS[0].src} className="absolute bottom-4 right-4 z-10 rounded-full bg-[rgba(0,0,0,0.45)] px-3 py-1 text-[11px] font-medium text-white backdrop-blur-sm hover:bg-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white" />

        {/* Scroll cue */}
        <motion.div style={reduce ? undefined : { opacity: cueOp }} className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-center text-white">
          <span className="block text-[11px] uppercase tracking-[0.2em] text-white/80">Scroll</span>
          <ChevronDown className="mx-auto mt-1 h-5 w-5 motion-safe:animate-bounce" aria-hidden="true" />
        </motion.div>
      </div>
    </section>
  );
};
